import { useMemo, useState, useEffect } from 'react'
import {
  useDepartmentTree,
  useDeleteDepartment,
  useDownloadDepartmentTemplate,
  useImportDepartment,
  useSaveOrUpdateDepartment,
} from '@/api/hooks/useOrganization'
import { useOrganizationStore } from '@/stores/organization'
import type { DepartmentTreeNode } from '@/api/types/organization.type'
import {
  Button,
  Input,
  Popover,
  PopoverContent,
  PopoverTrigger,
  toast,
} from '@you/ui'
import UpdateDialog from '@/components/Organization/UpdateDialog'
import ImportDialog from '@/components/common/ImportDialog'
import { Search, ChevronDown } from 'lucide-react'
import { downloadTemplate as downloadTemplateUtil } from '@/lib/utils'
import TreeItem from '@/components/Organization/TreeItem'

interface DepartmentTreeProps {
  teamId: string
}

const DepartmentTree = ({ teamId }: DepartmentTreeProps) => {
  const { currentDepartment, setCurrentDepartment, currentOrganization } =
    useOrganizationStore()
  const [expanded, setExpanded] = useState<Record<string, boolean>>({
    [currentOrganization?.id || '']: true,
  })
  const [keyword, setKeyword] = useState('')
  const { data, mutate } = useDepartmentTree({ teamId })
  const { trigger: deleteDept } = useDeleteDepartment()
  const { trigger: saveDept, isMutating } = useSaveOrUpdateDepartment()
  const { trigger: downloadTemplate } = useDownloadDepartmentTemplate()
  const { trigger: importDept } = useImportDepartment()

  // 弹窗状态
  const [dialogOpen, setDialogOpen] = useState(false)
  const [editing, setEditing] = useState<{
    id?: string
    name?: string
    parentId?: string
  } | null>(null)
  const [createPopoverOpen, setCreatePopoverOpen] = useState(false)
  const [importDialogOpen, setImportDialogOpen] = useState(false)
  const [editingId, setEditingId] = useState<string | undefined>(undefined)
  const [editingName, setEditingName] = useState('')
  const [editingParentId, setEditingParentId] = useState<string | undefined>(
    undefined
  )

  // 初始化当前部门是当前组织
  useEffect(() => {
    if (currentOrganization?.id) {
      const { id, name } = currentOrganization
      setCurrentDepartment({ id, name, parentId: '', teamId, isRoot: true })
    }
  }, [])

  const openCreateRoot = () => {
    setEditing({ id: undefined, name: '', parentId: '0' })
    setDialogOpen(true)
    setCreatePopoverOpen(false)
  }

  const handleDownloadTemplate = async () => {
    try {
      const result = await downloadTemplate()
      await downloadTemplateUtil(result)
      toast.success('模板下载成功')
    } catch {
      toast.error('模板下载失败')
    }
  }

  const handleImportDepartment = async (file: File) => {
    try {
      await importDept({ teamId, file })
      await mutate()
    } catch {
      toast.error('导入失败')
    }
  }

  const openImportDialog = () => {
    setImportDialogOpen(true)
    setCreatePopoverOpen(false)
  }

  // 开始编辑
  const handleStartEdit = (id: string, name: string, parentId: string) => {
    setEditingId(id)
    setEditingName(name)
    setEditingParentId(parentId)
  }

  // 取消编辑
  const handleCancelEdit = () => {
    setEditingId(undefined)
    setEditingName('')
    setEditingParentId(undefined)
  }

  // 确认编辑
  const handleConfirmEdit = async (id: string, name: string) => {
    if (name.trim() === '') {
      toast.error('部门名称不能为空')
      return
    }

    try {
      await saveDept({
        id,
        name: name.trim(),
        parentId: editingParentId,
        teamId,
      })
      await mutate()
      toast.success('修改成功')
      handleCancelEdit()
    } catch {
      toast.error('修改失败')
    }
  }

  const openUpdateChild = ({
    id,
    name,
    parentId,
  }: {
    id?: string
    name?: string
    parentId?: string
  }) => {
    setEditing({ id, name, parentId })
    setDialogOpen(true)
  }

  // 删除部门
  const handleDeleteDepartment = async (id: string) => {
    await deleteDept(id)
    // 提示删除成功
    toast.success('删除成功')
    await mutate()
  }

  const handleSubmitDepartment = async ({
    id,
    name,
    parentId,
    teamId,
  }: {
    id?: string
    name: string
    parentId?: string
    teamId?: string
  }) => {
    await saveDept({ id, name, parentId, teamId })
    setDialogOpen(false)
    setEditing(null)
    toast.success('保存成功')
    await mutate()
    if (parentId) setExpanded((prev) => ({ ...prev, [parentId]: true }))
  }

  const treeData = useMemo<DepartmentTreeNode[]>(() => {
    // 添加根节点组织信息
    const { id, name } = currentOrganization || {}
    const rootObj = {
      id: id || '',
      name: name || '',
      isRoot: true,
      subDepartments: data?.departments || [],
    } as DepartmentTreeNode
    return [rootObj]
  }, [data, currentOrganization])

  const filterTree = (
    nodes: DepartmentTreeNode[],
    term: string
  ): DepartmentTreeNode[] => {
    if (!term) return nodes
    const lower = term.toLowerCase()
    const loop = (arr: DepartmentTreeNode[]): DepartmentTreeNode[] => {
      const res: DepartmentTreeNode[] = []
      for (const n of arr) {
        const nameMatch = n.name.toLowerCase().includes(lower)
        const subDepartments = n.subDepartments ? loop(n.subDepartments) : []
        if (nameMatch || subDepartments.length > 0) {
          res.push({ ...n, subDepartments })
        }
      }
      return res
    }
    return loop(nodes)
  }

  const toggle = (id: string) =>
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }))
  const select = (node: DepartmentTreeNode) => {
    setCurrentDepartment({
      id: node.id,
      name: node.name,
      parentId: '',
      teamId,
      isRoot: !!node.isRoot,
    })
  }

  const renderTree = (
    nodes: DepartmentTreeNode[],
    level = 0,
    parentId: string = '0'
  ) => (
    <>
      {nodes.map((n) => (
        <div key={n.id}>
          <TreeItem
            node={n}
            level={level}
            expanded={expanded}
            onToggle={toggle}
            onSelect={select}
            onCreateChild={openUpdateChild}
            onDelete={handleDeleteDepartment}
            isSelected={currentDepartment?.id === n.id}
            editingId={editingId}
            editingName={editingName}
            setEditingName={setEditingName}
            onStartEdit={handleStartEdit}
            onCancelEdit={handleCancelEdit}
            onConfirmEdit={handleConfirmEdit}
            parentId={parentId}
          />
          {n.subDepartments && n.subDepartments.length > 0 && (
            <div
              className={`overflow-hidden transition-all duration-300 ease-in-out ${
                expanded[n.id]
                  ? 'max-h-[1000px] opacity-100'
                  : 'max-h-0 opacity-0'
              }`}
            >
              {renderTree(n.subDepartments, level + 1, n.isRoot ? '0' : n.id)}
            </div>
          )}
        </div>
      ))}
    </>
  )

  return (
    <div
      className="bg-white rounded-lg flex flex-col"
      style={{ height: '100%' }}
    >
      {/* 上 */}
      <div
        className="flex items-center justify-between px-4 h-[56px] border-b"
        style={{ borderColor: '#F3F2F2' }}
      >
        <div className="text-[20px] font-medium" style={{ color: '#3D1E82' }}>
          部门
        </div>
        <Popover open={createPopoverOpen} onOpenChange={setCreatePopoverOpen}>
          <PopoverTrigger asChild>
            <Button className="h-[32px] px-3 text-white flex items-center gap-1">
              新增
              <ChevronDown className="w-4 h-4" />
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-48 p-2">
            <div className="flex flex-col space-y-1">
              <button
                className="text-left px-3 py-2 rounded hover:bg-[#F7F1FF] text-[#666666] transition-colors duration-200"
                onClick={openCreateRoot}
              >
                新建
              </button>
              <button
                className="text-left px-3 py-2 rounded hover:bg-[#F7F1FF] text-[#666666] transition-colors duration-200"
                onClick={openImportDialog}
              >
                导入
              </button>
            </div>
          </PopoverContent>
        </Popover>
      </div>
      {/* 中 */}
      <div className="px-4 py-3" style={{ borderColor: '#F3F2F2' }}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <Input
            placeholder="搜索部门"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
            className="px-0 w-full pl-9 pr-2"
          />
        </div>
      </div>
      {/* 下 */}
      <div className="flex-1 overflow-auto">
        {!data ? (
          // 骨架屏
          <div className="p-4 space-y-3">
            {Array.from({ length: 8 }).map((_, index) => (
              <div key={index} className="flex items-center space-x-3">
                <div className="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
                <div className="flex-1">
                  <div
                    className="h-4 bg-gray-200 rounded animate-pulse"
                    style={{ width: `${60 + Math.random() * 40}%` }}
                  ></div>
                </div>
                <div className="w-4 h-4 bg-gray-200 rounded animate-pulse"></div>
              </div>
            ))}
          </div>
        ) : (
          renderTree(filterTree(treeData, keyword))
        )}
      </div>

      {/* 新增/编辑部门弹窗复用 */}
      <UpdateDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        entity="department"
        id={editing?.id}
        initialName={editing?.name}
        parentId={editing?.parentId}
        teamId={teamId}
        loading={isMutating}
        onSubmit={handleSubmitDepartment}
      />

      {/* 导入部门对话框 */}
      <ImportDialog
        open={importDialogOpen}
        onOpenChange={setImportDialogOpen}
        title="导入部门"
        onImport={handleImportDepartment}
        onDownloadTemplate={handleDownloadTemplate}
        accept=".xlsx,.xls"
        maxSize={10}
      />
    </div>
  )
}

export default DepartmentTree
